import { useState } from 'react';
import { StackLayout, Icon } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { TextFormField } from 'components/form-fields/text-form-field/text-form-field.component';
import { getTodayLocalDate } from 'utils/formatting';
import {
  FormHandlers,
  PersonalInformationData,
  PersonalInformationDataError,
} from '../../types/onboarding-account-setup-types';

interface PersonalInformationInputProps {
  value: PersonalInformationData;
  onChange: FormHandlers['handlePersonalInformationChange'];
  errors?: PersonalInformationDataError;
}

export const PersonalInformationInput = ({
  errors,
  onChange,
  value,
}: PersonalInformationInputProps) => {
  const { formatMessage } = useIntl();
  const [isDobActive, setIsDobActive] = useState(false);
  const showDatePicker = isDobActive || Boolean(value.dateOfBirth);
  const today = getTodayLocalDate();

  return (
    <StackLayout spacing="$one" css={{ width: '100%' }}>
      <TextFormField
        id="personal-info-first-name"
        name="firstName"
        label={formatMessage({
          id: 'STR_ONBOARDING_ACCOUNT_SETUP_PERSONAL_INFO_FIRST_NAME_LABEL',
        })}
        value={value.firstName}
        onChange={(e) => onChange('firstName', e.target.value)}
        error={
          errors?.firstName
            ? formatMessage({ id: errors.firstName })
            : undefined
        }
        required
      />

      <TextFormField
        id="personal-info-middle-name"
        name="middleName"
        label={formatMessage({
          id: 'STR_ONBOARDING_ACCOUNT_SETUP_PERSONAL_INFO_MIDDLE_NAME_LABEL',
        })}
        value={value.middleName}
        onChange={(e) => onChange('middleName', e.target.value)}
      />

      <TextFormField
        id="personal-info-last-name"
        name="lastName"
        label={formatMessage({
          id: 'STR_ONBOARDING_ACCOUNT_SETUP_PERSONAL_INFO_LAST_NAME_LABEL',
        })}
        value={value.lastName}
        onChange={(e) => onChange('lastName', e.target.value)}
        error={
          errors?.lastName ? formatMessage({ id: errors.lastName }) : undefined
        }
        required
      />

      <TextFormField
        id="personal-info-dob"
        name="dateOfBirth"
        label={formatMessage({
          id: 'STR_ONBOARDING_ACCOUNT_SETUP_PERSONAL_INFO_DOB_LABEL',
        })}
        type={showDatePicker ? 'date' : 'text'}
        value={value.dateOfBirth}
        max={today}
        onFocus={(e) => {
          setIsDobActive(true);
          try {
            e.currentTarget.showPicker();
          } catch (error) {
            console.warn('showPicker() not supported:', error);
          }
        }}
        onBlur={() => setIsDobActive(false)}
        onChange={(e) => onChange('dateOfBirth', e.target.value)}
        placeholder={formatMessage({
          id: 'STR_ONBOARDING_ACCOUNT_SETUP_PERSONAL_INFO_DOB_PLACEHOLDER',
        })}
        leadingContent={<Icon icon="descriptiveCalendar" />}
        trailingContent={<Icon icon="interfaceChevronDown" />}
        error={
          errors?.dateOfBirth
            ? formatMessage({ id: errors.dateOfBirth })
            : undefined
        }
        css={{
          '.GDS-base-input': {
            cursor: 'pointer',
          },
          '::-webkit-calendar-picker-indicator': {
            opacity: 0,
            position: 'absolute',
            right: 0,
            width: '100%',
            height: '100%',
            cursor: 'pointer',
          },
        }}
        required
      />
    </StackLayout>
  );
};
